import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, MessagesSquare, Send } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useApp';
import { useSeo } from '@/hooks/useSeo';
import { SITE_NAME } from '@/lib/seo';
import { formatPrice, formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import { Button, EmptyState, Spinner, Textarea } from '@/components/ui';
import { Photo } from '@/components/Photo';
import type { ConversationSummary, ConversationThread } from '@/lib/types';

/**
 * How often an open conversation asks for new messages.
 *
 * Plain polling rather than a socket. SignalR is in the stack and is the obvious upgrade, but its
 * browser client is an npm package, and adding a dependency to make a chat feel four seconds
 * fresher is a poor trade against something that works today. Five seconds while a thread is open,
 * and nothing at all when the tab is in the background — React Query stops polling a window nobody
 * is looking at, which is most of what makes polling defensible.
 */
const THREAD_POLL_MS = 5_000;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

function Avatar({ name, url, className }: { name: string; url?: string | null; className?: string }) {
  if (url) {
    return <img src={url} alt="" loading="lazy" className={cn('rounded-full object-cover', className)} />;
  }

  return (
    <span
      className={cn(
        'flex items-center justify-center rounded-full bg-accent-soft text-[11px] font-bold text-accent',
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}

export function InboxPage() {
  useSeo({ title: `Poruke | ${SITE_NAME}`, noIndex: true });

  const { data, isPending } = useQuery({
    queryKey: ['inbox'],
    queryFn: () => api<ConversationSummary[]>('/api/messages'),
    refetchInterval: 20_000,
  });

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="size-8 text-accent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="mb-6 text-2xl font-bold text-body">Poruke</h1>

      {data && data.length > 0 ? (
        <ul className="space-y-2">
          {data.map((conversation, index) => (
            <li
              key={conversation.id}
              style={{ '--i': Math.min(index, 9) } as React.CSSProperties}
              className="animate-fade-in-up stagger"
            >
              <Link
                to={`/poruke/${conversation.id}`}
                className={cn(
                  'lift flex items-center gap-3 rounded-[var(--radius-panel)] border bg-raised p-3 shadow-card',
                  conversation.unreadCount > 0 ? 'border-accent/40' : 'border-default',
                )}
              >
                <Photo
                  src={conversation.listingThumbnailUrl}
                  alt=""
                  className="size-16 shrink-0 rounded-xl"
                  fallback={<Avatar name={conversation.counterpartyName} className="size-9" />}
                />

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-body">
                      {conversation.counterpartyName}
                    </span>
                    <span className="ml-auto shrink-0 text-[11px] text-faint">
                      {formatRelative(conversation.lastMessageAt)}
                    </span>
                  </span>

                  <span className="mt-0.5 block truncate text-[11px] text-faint">
                    {conversation.listingTitle} · {formatPrice(conversation.listingPrice)}
                  </span>

                  <span
                    className={cn(
                      'mt-1 block truncate text-[13px]',
                      conversation.unreadCount > 0 ? 'font-medium text-body' : 'text-muted',
                    )}
                  >
                    {conversation.lastMessagePreview ?? '—'}
                  </span>
                </span>

                {conversation.unreadCount > 0 && (
                  <span className="animate-pop-in flex size-5 shrink-0 items-center justify-center rounded-full bg-accent text-[10px] font-bold text-on-accent">
                    {conversation.unreadCount}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon={<MessagesSquare size={40} />}
          title="Nema poruka"
          description="Kada pošaljete pitanje prodavcu ili neko pita za vaš oglas, razgovor se pojavljuje ovde."
          action={
            <Link to="/">
              <Button>Pretraži oglase</Button>
            </Link>
          }
        />
      )}
    </div>
  );
}

export function ConversationPage() {
  const { conversationId = '' } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [body, setBody] = useState('');
  const bottom = useRef<HTMLDivElement>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: ['thread', conversationId],
    queryFn: () => api<ConversationThread>(`/api/messages/${conversationId}`),
    refetchInterval: THREAD_POLL_MS,
  });

  const messageCount = data?.messages.length ?? 0;

  // Scroll on arrival and on every new message, but keyed on the count rather than the array — the
  // poll returns a fresh array every five seconds and would otherwise yank the view back down while
  // somebody is reading further up.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messageCount]);

  useEffect(() => {
    if (!conversationId || messageCount === 0) {
      return;
    }

    api(`/api/messages/${conversationId}/read`, { method: 'POST' })
      .then(() => queryClient.invalidateQueries({ queryKey: ['unread-messages'] }))
      .catch(() => undefined);
  }, [conversationId, messageCount, queryClient]);

  const send = useMutation({
    mutationFn: () =>
      api<ConversationThread>('/api/messages', {
        method: 'POST',
        // The thread id, not the listing id: a seller can have several buyers on one car, so only
        // the conversation identifies where a reply belongs.
        body: { conversationId, body },
      }),
    onSuccess: (thread) => {
      setBody('');
      queryClient.setQueryData(['thread', conversationId], thread);
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });

  /*
    Blocking, from inside the thread.

    This is where somebody decides they are done with a person, so this is where the control belongs
    — not several screens away in settings, which is only where the list of them is undone. Sending
    is refused in both directions afterwards, so there is nothing useful left on this screen and the
    inbox is where they are put.
  */
  const block = useMutation({
    mutationFn: (counterpartyId: string) =>
      api(`/api/blocks/${counterpartyId}`, { method: 'PUT' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inbox'] });
      queryClient.invalidateQueries({ queryKey: ['blocked-users'] });
      navigate('/poruke');
    },
  });

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner className="size-8 text-accent" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <EmptyState
          title="Razgovor nije pronađen"
          description="Možda je uklonjen, ili ne pripada vašem nalogu."
          action={
            <Button onClick={() => navigate('/poruke')}>Nazad na poruke</Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col px-4 py-6 pb-[calc(10rem_+_var(--safe-bottom))] lg:pb-6">
      <header className="mb-4 flex items-center gap-3">
        <Link
          to="/poruke"
          aria-label="Nazad na poruke"
          className="press flex size-9 shrink-0 items-center justify-center rounded-xl text-muted transition hover:bg-sunken hover:text-body"
        >
          <ArrowLeft size={18} />
        </Link>

        <Avatar
          name={data.counterpartyName}
          url={data.counterpartyPhotoUrl}
          className="size-10 shrink-0"
        />

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-body">{data.counterpartyName}</p>
          <Link
            to={`/oglas/${data.listingShortCode}`}
            className="truncate text-[11px] text-faint transition hover:text-accent"
          >
            {data.listingTitle} · {formatPrice(data.listingPrice)}
          </Link>
        </div>

        <button
          type="button"
          onClick={() => {
            if (
              window.confirm(
                `Blokirati korisnika ${data.counterpartyName}? Nakon toga nećete moći da razmenjujete poruke. Blokadu možete ukinuti u podešavanjima.`,
              )
            ) {
              block.mutate(data.counterpartyId);
            }
          }}
          disabled={block.isPending}
          title="Blokiraj korisnika"
          aria-label="Blokiraj korisnika"
          className="press flex size-9 shrink-0 items-center justify-center rounded-xl text-faint transition hover:bg-sunken hover:text-danger"
        >
          <Ban size={17} />
        </button>
      </header>

      <ol className="space-y-2">
        {data.messages.map((message, index) => (
          <li
            key={message.id}
            style={{ '--i': Math.min(index, 6) } as React.CSSProperties}
            className={cn('animate-fade-in-up stagger flex', message.isMine ? 'justify-end' : 'justify-start')}
          >
            <div
              className={cn(
                'max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap',
                message.isMine
                  ? 'rounded-br-md bg-accent text-on-accent'
                  : 'rounded-bl-md border border-default bg-raised text-body',
              )}
            >
              {message.body}
              <span
                className={cn(
                  'mt-1 block text-[10px]',
                  message.isMine ? 'text-on-accent/70' : 'text-faint',
                )}
              >
                {formatRelative(message.sentAt)}
                {message.isMine && message.readAt && ' · pročitano'}
              </span>
            </div>
          </li>
        ))}
        <div ref={bottom} />
      </ol>

      {/*
        Pinned on phones for the same reason the listing page pins its phone number: the reply box
        is the point of the screen, and it should not be at the bottom of a long scroll.
      */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (body.trim()) {
            send.mutate();
          }
        }}
        className="page-action-bar glass fixed inset-x-0 z-30 border-t border-default px-4 py-2.5 lg:static lg:mt-6 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none"
      >
        <div className="mx-auto flex max-w-3xl items-end gap-2">
          <Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, shift-enter breaks the line. Anything else and a chat feels like a form.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                if (body.trim()) {
                  send.mutate();
                }
              }
            }}
            placeholder="Napišite poruku…"
            maxLength={4000}
            rows={1}
            className="max-h-32 min-h-11 flex-1 resize-none py-2.5 text-sm"
          />

          <Button
            type="submit"
            size="sm"
            className="shrink-0"
            disabled={!body.trim() || !user}
            loading={send.isPending}
            aria-label="Pošalji"
          >
            <Send size={16} />
          </Button>
        </div>
      </form>
    </div>
  );
}
